import { DestroyRef, Injectable } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { CookieKeys } from '@core/const';
import { CookieService } from '@core/feature';
import { Infoblock } from '@promo/const';
import { PromoService } from '@promo/data-access';
import { CurrentProfileService } from '@user/feature';
import { BehaviorSubject, combineLatest, map } from 'rxjs';

@Injectable({ providedIn: 'root' })
export class InfoblockService {
  infoblocks$ = new BehaviorSubject<Infoblock[]>([]);

  constructor(
    private currentProfileService: CurrentProfileService,
    private promoService: PromoService,
    private cookieService: CookieService,
    private destroyRef: DestroyRef,
  ) {}

  init(): void {
    this.updateInfoblocks();
  }

  visitedMarker(infoblock: Infoblock): void {
    this.updateCookieInfoblocks(infoblock);
    this.updateInfoblocks();
  }

  private updateCookieInfoblocks(infoblock: Infoblock): void {
    const cookieInfoblock = this.cookieService.get(CookieKeys.Infoblock);
    const infoblocks = cookieInfoblock ? (JSON.parse(cookieInfoblock) as Infoblock[]) : [];

    infoblocks.push(infoblock);
    this.cookieService.put(CookieKeys.Infoblock, JSON.stringify(infoblocks), { path: '/' });
  }

  private checkVisited(infoblock: Infoblock): boolean {
    const cookieInfoblock = this.cookieService.get(CookieKeys.Infoblock);

    if (!cookieInfoblock) return false;

    const infoblocks = JSON.parse(cookieInfoblock) as Infoblock[];

    return infoblocks.some((ib) => ib.id === infoblock.id);
  }

  private updateInfoblocks(): void {
    combineLatest({
      profile: this.currentProfileService.get(),
      promo: this.promoService.getPromo(),
    })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        map(({ profile, promo }) => {
          if (promo && profile) {
            return promo.infoblocks
              ?.sort((a, b) => (a.showFirst === b.showFirst ? 0 : a.showFirst ? -1 : 1))
              .filter((ib) => {
                const isAuthorizedUser = ib.userType === 'authorized' && profile.isLoggedIn;
                const isNotAuthorizedUser = ib.userType === 'notAuthorized' && !profile.isLoggedIn;
                const isAllUserType = ib.userType === 'all';

                const isVisitedIb = this.checkVisited(ib);

                if (isVisitedIb) return false;

                if (ib.userSegmentCode) {
                  return profile.bonusBalance && ib.userSegmentCode === profile.bonusBalance.segmentCode;
                }

                return isAuthorizedUser || isNotAuthorizedUser || isAllUserType;
              });
          }

          return [];
        }),
      )
      .subscribe((infoblocks) => {
        this.infoblocks$.next(infoblocks || []);
      });
  }
}
